"use client";

import Link from "@/components/ui/LocaleLink";
import { useTranslations } from "next-intl";
import { SignIn } from "@phosphor-icons/react/dist/ssr";
import { stripLocalePrefix } from "@/i18n/routing";
import { useClientSnapshot } from "@/lib/hooks/use-client-snapshot";

const AUTH_ROUTES = ["/login", "/signup", "/reset", "/verify"];

/**
 * Login link that preserves the current location as `?next=`. The path is read
 * client-side (window) rather than via usePathname() so this stays
 * prerenderable on dynamic routes under cacheComponents (usePathname is dynamic
 * data there — the segment value isn't known at build). SSR/prerender ships a
 * plain "/login"; `useClientSnapshot` upgrades it to include `next` after
 * hydration (no effect/setState — see the hook's SSR-safe read pattern).
 */
export default function LoginLink({ className }: { className?: string }) {
  const t = useTranslations("nav");
  const href = useClientSnapshot(() => {
    const path = window.location.pathname;
    // Strip the locale prefix (/vi, /fr) before the auth-page check; `returnTo`
    // below keeps the full path so post-login returns to the same locale.
    const localePath = stripLocalePrefix(path);
    if (AUTH_ROUTES.some((r) => localePath.startsWith(r))) return "/login";
    const returnTo = `${path}${window.location.search}`;
    return `/login?next=${encodeURIComponent(returnTo)}`;
  }, "/login");

  return (
    <Link href={href} className={className}>
      <SignIn size={15} weight="bold" aria-hidden="true" />
      {t("login")}
    </Link>
  );
}
